import React from 'react';
import { motion } from 'framer-motion';
import { PlayIcon, RocketIcon, UsersIcon, TrophyIcon, CoinsIcon } from 'lucide-react';
export function Hero() {
  const stats = [{
    icon: UsersIcon,
    value: '2,400+',
    label: 'Clans Active'
  }, {
    icon: TrophyIcon,
    value: '18,000+',
    label: 'Matches Tracked'
  }, {
    icon: CoinsIcon,
    value: '€1.2M+',
    label: 'Prizes Managed'
  }];
  const headlineWords = ['COMMAND', 'YOUR', 'CLAN.', 'DOMINATE', 'THE', 'ARENA.'];
  return <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden" data-id="element-37">
      {/* Background */}
      <div className="absolute inset-0 bg-gray-950 bg-grid-pattern" data-id="element-38" />

      {/* Animated gradient orbs */}
      <motion.div animate={{
      scale: [1, 1.2, 1],
      opacity: [0.3, 0.5, 0.3]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" data-id="element-39" />
      <motion.div animate={{
      scale: [1.2, 1, 1.2],
      opacity: [0.2, 0.4, 0.2]
    }} transition={{
      duration: 10,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fuchsia-500/20 rounded-full blur-3xl" data-id="element-40" />

      {/* Floating geometric shapes */}
      <motion.div animate={{
      y: [-20, 20, -20],
      rotate: [0, 180, 360]
    }} transition={{
      duration: 20,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute top-1/3 right-1/4 w-16 h-16 border border-cyan-500/30 rotate-45" data-id="element-41" />
      <motion.div animate={{
      y: [20, -20, 20],
      rotate: [360, 180, 0]
    }} transition={{
      duration: 15,
      repeat: Infinity,
      ease: 'linear'
    }} className="absolute bottom-1/3 left-1/4 w-12 h-12 border border-fuchsia-500/30 rotate-12" data-id="element-42" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-43">
        {/* Headline */}
        <h1 className="font-display font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl leading-tight mb-6" data-id="element-44">
          <span className="flex flex-wrap justify-center gap-x-4 gap-y-2" data-id="element-45">
            {headlineWords.map((word, index) => <motion.span key={index} initial={{
            opacity: 0,
            y: 50
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={word === 'CLAN.' || word === 'ARENA.' ? 'text-cyan-400 text-glow-cyan' : 'text-white'} data-id="element-46">
                {word}
              </motion.span>)}
          </span>
        </h1>

        {/* Subheadline */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.8
      }} className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto mb-10" data-id="element-47">
          The all-in-one operations hub for esports organizations. Build
          rosters, design jerseys, run tournaments — from scrims to grand
          finals.
        </motion.p>

        {/* CTAs */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 1
      }} className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16" data-id="element-48">
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="px-8 py-4 bg-cyan-500 text-gray-950 font-bold text-sm uppercase tracking-wider rounded clip-corner-br glow-cyan-strong hover:bg-cyan-400 transition-colors flex items-center gap-2" data-id="element-49">
            <RocketIcon className="w-5 h-5" data-id="element-50" />
            Deploy Your Org
          </motion.button>
          <motion.button whileHover={{
          scale: 1.02
        }} whileTap={{
          scale: 0.98
        }} className="px-8 py-4 border border-cyan-500/50 text-cyan-400 font-bold text-sm uppercase tracking-wider rounded clip-corner-br hover:bg-cyan-500/10 transition-colors flex items-center gap-2" data-id="element-51">
            <PlayIcon className="w-5 h-5" data-id="element-52" />
            Watch Demo
          </motion.button>
        </motion.div>

        {/* Stats Bar */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 1.2
      }} className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16" data-id="element-53">
          {stats.map((stat, index) => <motion.div key={stat.label} initial={{
          opacity: 0,
          scale: 0.8
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.4,
          delay: 1.4 + index * 0.1
        }} className="flex items-center gap-3" data-id="element-54">
              <div className="w-12 h-12 bg-gray-900/80 border border-cyan-500/20 rounded-lg flex items-center justify-center" data-id="element-55">
                <stat.icon className="w-6 h-6 text-cyan-400" data-id="element-56" />
              </div>
              <div className="text-left" data-id="element-57">
                <div className="font-display font-bold text-2xl text-white" data-id="element-58">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-500 uppercase tracking-wider" data-id="element-59">
                  {stat.label}
                </div>
              </div>
            </motion.div>)}
        </motion.div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-950 to-transparent" data-id="element-60" />
    </section>;
}